/**
 * Executive control tower. MASTER_PROMPT section 8.1.
 *
 * Three questions in order: are we okay, why or why not, and what exactly
 * needs action. The KPI row answers the first, the charts the second, the
 * tables the third — and nothing on this page is here for decoration.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { ExecutiveBoard } from "../../lib/types";
import { fmtDate, fmtPct, fmtQty, toNumber } from "../../lib/format";
import { KpiCard } from "./board";
import { PeriodPicker } from "../../components/PeriodPicker";
import { comparisonLabel, describePeriod } from "../../lib/period";
import { useCompare, useReportPeriod } from "../../lib/reportPeriod";
import { ReportDownload } from "../../components/ReportDownload";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";

type ReasonRow = ExecutiveBoard["reason_distribution"][number];
type UnreconciledRow = ExecutiveBoard["unreconciled"][number];

const REASON_COLUMNS: SheetColumn<ReasonRow>[] = [
  { key: "reason", label: "Reason", value: (r) => r.reason },
  { key: "runs", label: "Runs", type: "number", numeric: true,
    value: (r) => r.runs },
  { key: "qty", label: "Quantity", type: "number", numeric: true,
    value: (r) => toNumber(r.qty), format: (r) => `${fmtQty(r.qty)} kg` },
];

const UNRECONCILED_COLUMNS: SheetColumn<UnreconciledRow>[] = [
  { key: "machine", label: "Machine", value: (r) => r.machine },
  { key: "date", label: "Date", type: "date", value: (r) => r.business_date,
    format: (r) => fmtDate(r.business_date) },
  { key: "shift", label: "Shift",
    value: (r) => (r.shift === "DAY" ? "Day" : "Night") },
  { key: "unaccounted", label: "Unaccounted", type: "number", numeric: true,
    value: (r) => toNumber(r.variance_qty),
    format: (r) => fmtQty(r.variance_qty) },
  { key: "variance", label: "Variance", type: "number", numeric: true,
    value: (r) => toNumber(r.variance_pct),
    format: (r) => fmtPct(r.variance_pct) },
  // Filterable on the word the screen shows, so "show me the ones with no
  // reason" is two clicks rather than a read of the whole column.
  { key: "reason", label: "Reason",
    value: (r) => (r.has_reason ? "given" : "missing") },
];

export function Executive() {
  const [period, setPeriod] = useReportPeriod();
  const [compare, setCompare] = useCompare();
  const [board, setBoard] = useState<ExecutiveBoard | null>(null);
  const reasons = useSheet(board?.reason_distribution ?? [], REASON_COLUMNS);
  const unreconciled = useSheet(board?.unreconciled ?? [],
                                UNRECONCILED_COLUMNS);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setBoard(null);
    setError(null);
    api
      .executive(period, compare)
      .then(setBoard)
      .catch((e: ApiError) => setError(e.message));
  }, [period, compare]);

  useEffect(load, [load]);

  const funnelMax = board
    ? Math.max(...board.funnel.map((f) => Number(f.qty)), 1)
    : 1;
  const heatMax = board
    ? Math.max(
        ...board.variance_heatmap.map((v) => Math.abs(Number(v.variance_qty))),
        1
      )
    : 1;

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Plant control tower</h2>
          <p className="page__sub">
            {describePeriod(period)} · Are we okay, why, and what needs
            action — in that order
          </p>
        </div>
        <div className="page__controls">
          <PeriodPicker value={period} onChange={setPeriod} shifts
                        compare={compare} onCompare={setCompare} />
          <ReportDownload title="Plant control tower" period={period} />
        </div>
      </header>

      {error && <div className="banner banner--negative">{error}</div>}

      {!board && !error && (
        <>
          <div className="kpirow">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton kpi" style={{ height: 108 }} />
            ))}
          </div>
          <div className="skeleton state__bar" style={{ height: 220 }} />
        </>
      )}

      {board && (
        <>
          <div className="kpirow">
            {board.kpis.map((k) => (
              <KpiCard key={k.key} kpi={k} vs={comparisonLabel(period, compare)} />
            ))}
          </div>

          {board.exceptions.length > 0 && (
            <section className="card exceptions">
              <h3 className="section-title">Needs attention</h3>
              <ul className="exceptions__list">
                {board.exceptions.map((e) => (
                  <li key={e.key}>
                    <span className="pill pill--warning">{e.count}</span>
                    {e.message}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="board2">
            {/* Where the plant chokes */}
            <section className="card">
              <h3 className="section-title">Production funnel</h3>
              <p className="section-note">
                Where the chain narrows in this period
              </p>
              <div className="funnel">
                {board.funnel.map((stage) => (
                  <div key={stage.stage} className="funnel__row">
                    <span className="funnel__label">{stage.stage}</span>
                    <div className="funnel__track">
                      <div
                        className="funnel__bar"
                        style={{
                          width: `${Math.max(
                            (Number(stage.qty) / funnelMax) * 100,
                            0.5
                          )}%`,
                        }}
                      />
                    </div>
                    <span className="funnel__value num mono">
                      {fmtQty(stage.qty)}{" "}
                      <span className="funnel__unit">{stage.unit}</span>
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* The instrument that finds the leak */}
            <section className="card">
              <h3 className="section-title">Unaccounted material by machine</h3>
              <p className="section-note">
                The 7,163 kg question, machine by machine
              </p>
              {board.variance_heatmap.length === 0 ? (
                <p className="section-note">No production recorded.</p>
              ) : (
                <div className="heat">
                  {board.variance_heatmap.slice(0, 12).map((row) => {
                    const intensity =
                      Math.abs(Number(row.variance_qty)) / heatMax;
                    return (
                      <div key={row.machine} className="heat__row">
                        <span className="heat__label">{row.machine}</span>
                        <div className="heat__track">
                          <div
                            className="heat__bar"
                            style={{
                              width: `${Math.max(intensity * 100, 1)}%`,
                              opacity: 0.35 + intensity * 0.65,
                            }}
                          />
                        </div>
                        <span className="heat__value num mono">
                          {fmtQty(row.variance_qty)} kg
                        </span>
                        <span className="heat__pct num mono">
                          {row.variance_pct === null
                            ? "—"
                            : fmtPct(row.variance_pct)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          {board.reason_distribution.length > 0 && (
            <section className="card">
              <h3 className="section-title">Where the material went</h3>
              <p className="section-note">
                Reason codes recorded at entry — the only way to answer this
              </p>
              <div className="tablewrap">
                <SheetToolbar sheet={reasons} exportName="reason-codes" />
                <table className="table">
                  <SheetHead sheet={reasons} />
                  <tbody>
                    {reasons.rows.map((r) => (
                      <tr key={r.reason}>
                        <td>{r.reason}</td>
                        <td className="num mono">{r.runs}</td>
                        <td className="num mono">{fmtQty(r.qty)} kg</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="card">
            <h3 className="section-title">Today&rsquo;s action list</h3>
            <p className="section-note">
              Shifts awaiting verification, largest unaccounted quantity first
            </p>
            {board.unreconciled.length === 0 ? (
              <p className="section-note">
                Nothing waiting — every submitted shift has been verified.
              </p>
            ) : (
              <div className="tablewrap">
                <SheetToolbar sheet={unreconciled} exportName="action-list" />
                <table className="table">
                  <SheetHead sheet={unreconciled} />
                  <tbody>
                    {unreconciled.rows.map((r) => (
                      <tr key={r.run_id}>
                        <td>{r.machine}</td>
                        <td>{fmtDate(r.business_date)}</td>
                        <td>{r.shift === "DAY" ? "Day" : "Night"}</td>
                        <td className="num mono cell--negative">
                          ▲ {fmtQty(r.variance_qty)}
                        </td>
                        <td className="num mono">{fmtPct(r.variance_pct)}</td>
                        <td>
                          {r.has_reason ? (
                            <span className="pill">given</span>
                          ) : (
                            <span className="pill pill--warning">missing</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
